import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Landmark, Shield, TrendingUp, Clock, Percent, Lock, ArrowRight, CheckCircle, Calendar, Banknote, Calculator } from 'lucide-react';

const FixedDepositsPage = () => {
  const [amount, setAmount] = useState(100000);
  const [tenure, setTenure] = useState(12);
  const [rate] = useState(7.5);

  const calculateMaturity = () => {
    const principal = amount;
    const r = rate / 100;
    const n = 4; // quarterly compounding
    const t = tenure / 12;
    const maturityAmount = principal * Math.pow((1 + r/n), n * t);
    return maturityAmount.toFixed(0);
  };

  const calculateInterest = () => {
    return (calculateMaturity() - amount).toFixed(0);
  };

  const fdOptions = [
    {
      bank: "High Yield FD",
      rate: "8.5%",
      tenure: "1-5 Years",
      minAmount: "₹10,000",
      highlight: true
    },
    {
      bank: "Tax Saver FD",
      rate: "7.5%",
      tenure: "5 Years Lock-in",
      minAmount: "₹1,000",
      highlight: false
    },
    {
      bank: "Senior Citizen FD",
      rate: "8.0%",
      tenure: "1-10 Years",
      minAmount: "₹5,000",
      highlight: false
    },
    {
      bank: "Flexi FD",
      rate: "7.0%",
      tenure: "7 Days - 10 Years",
      minAmount: "₹25,000",
      highlight: false
    }
  ];

  const benefits = [
    { icon: Shield, title: "Capital Protection", desc: "Your principal amount is completely safe" },
    { icon: Percent, title: "Guaranteed Returns", desc: "Fixed interest rate throughout the tenure" },
    { icon: Lock, title: "DICGC Insured", desc: "Deposits insured up to ₹5 lakhs per bank" },
    { icon: Calendar, title: "Flexible Tenure", desc: "Choose from 7 days to 10 years" },
    { icon: Banknote, title: "Regular Income", desc: "Opt for monthly, quarterly, or yearly payouts" },
    { icon: TrendingUp, title: "Tax Benefits", desc: "Save up to ₹1.5L under Section 80C" }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-cyan-500/10" />
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-400/10 border border-blue-400/30 rounded-full mb-6">
              <Landmark className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Safe & Secure Investment</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Fixed Deposits
              <span className="block text-yellow-400">Guaranteed Returns</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Park your savings in high-interest FDs from leading banks. 
              Enjoy assured returns with complete capital protection.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
              >
                Open FD Now <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                Compare Rates
              </motion.button>
            </div>
          </motion.div>

          {/* Interest Rate Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { label: "Up to", value: "8.5%", suffix: "p.a." },
              { label: "Min Investment", value: "₹1,000", suffix: "" },
              { label: "Tenure Options", value: "7 Days", suffix: "to 10 Years" },
              { label: "DICGC Cover", value: "₹5 Lakh", suffix: "per bank" }
            ].map((item, index) => (
              <div key={index} className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 text-center">
                <div className="text-gray-400 text-sm">{item.label}</div>
                <div className="text-2xl md:text-3xl font-bold text-yellow-400">{item.value}</div>
                <div className="text-gray-500 text-xs">{item.suffix}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FD Calculator */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Calculator className="w-8 h-8 text-yellow-400" />
                <h2 className="text-3xl font-bold text-white">FD Calculator</h2>
              </div>
              <p className="text-gray-400 mb-8">
                Calculate your maturity amount and interest earnings
              </p>

              <div className="space-y-8">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-white font-medium">Investment Amount</label>
                    <span className="text-yellow-400 font-bold">₹{amount.toLocaleString()}</span>
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="1000000"
                    step="10000"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                  />
                  <div className="flex justify-between text-gray-500 text-sm mt-1">
                    <span>₹10,000</span>
                    <span>₹10,00,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-white font-medium">Tenure (Months)</label>
                    <span className="text-yellow-400 font-bold">{tenure} Months</span>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="120"
                    step="3"
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                  />
                  <div className="flex justify-between text-gray-500 text-sm mt-1">
                    <span>3 Months</span>
                    <span>10 Years</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-white font-medium">Interest Rate</label>
                    <span className="text-green-400 font-bold">{rate}% p.a.</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8"
            >
              <h3 className="text-xl font-bold text-white mb-8 text-center">Your FD Summary</h3>
              
              <div className="space-y-6">
                <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                  <span className="text-gray-400">Principal Amount</span>
                  <span className="text-white font-bold text-xl">₹{amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-black/50 rounded-xl">
                  <span className="text-gray-400">Interest Earned</span>
                  <span className="text-green-400 font-bold text-xl">₹{Number(calculateInterest()).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-yellow-400/10 border border-yellow-400/30 rounded-xl">
                  <span className="text-yellow-400 font-medium">Maturity Amount</span>
                  <span className="text-yellow-400 font-bold text-2xl">₹{Number(calculateMaturity()).toLocaleString()}</span>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full mt-8 px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all"
              >
                Open This FD <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FD Options */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Choose Your FD Type
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Select from various FD options to match your financial goals
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fdOptions.map((fd, index) => (
              <motion.div
                key={fd.bank}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`bg-zinc-900 border rounded-2xl p-6 transition-all cursor-pointer ${
                  fd.highlight ? 'border-yellow-400/50 ring-1 ring-yellow-400/20' : 'border-zinc-800 hover:border-yellow-500/50'
                }`}
              >
                {fd.highlight && (
                  <div className="inline-block px-3 py-1 bg-yellow-400/20 text-yellow-400 text-xs font-medium rounded-full mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-4">{fd.bank}</h3>
                <div className="text-3xl font-bold text-green-400 mb-4">{fd.rate}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tenure</span>
                    <span className="text-gray-300">{fd.tenure}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Min. Amount</span>
                    <span className="text-gray-300">{fd.minAmount}</span>
                  </div>
                </div>
                <button className="w-full mt-6 py-3 bg-white/10 text-white font-medium rounded-lg hover:bg-yellow-400 hover:text-black transition-all">
                  Invest Now
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Benefits of Fixed Deposits
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 bg-zinc-900/50 rounded-xl border border-zinc-800"
              >
                <div className="w-12 h-12 rounded-lg bg-blue-400/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border border-blue-400/30 rounded-3xl p-12 text-center"
          >
            <Shield className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Secure Your Future Today
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Open a Fixed Deposit and enjoy guaranteed returns with complete peace of mind. 
              Your money grows safely while you sleep.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
            >
              Open FD Now <ArrowRight className="inline ml-2 w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FixedDepositsPage;
